"use client";
import { ReactNode, useContext, useEffect, useRef, useState } from "react";
import { AuthContext } from "../context/AuthContext";
import axios from "../config/axios";
import { CurrencyDisplay } from "../utils/currency";

interface SiteChromeProps {
  children: ReactNode;
}

interface StoreConfigLite {
  branding?: { logo?: string };
  shipping?: { minOrderForFreeShipping?: number };
  currency?: { code?: string; symbol?: string };
}

export default function SiteChrome({ children }: SiteChromeProps) {
  const authContext = useContext(AuthContext);
  const isLoggedIn = !!authContext?.user;

  const [footerHtml, setFooterHtml] = useState<string>("");
  const [storeConfig, setStoreConfig] = useState<StoreConfigLite>({});
  const [cartCount, setCartCount] = useState<number>(0);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const footerResponse = await axios.get("/cms/render?templates=footer");
        const sc = footerResponse.data?.storeConfig || {};
        setStoreConfig(sc);
        setFooterHtml(
          footerResponse.data?.templates?.filter((t: any) => t.type === "footer")[0]?.content || ""
        );
      } catch (error) {
        console.error("Error fetching templates:", error);
      }
    };
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (!isLoggedIn) {
      setCartCount(0);
      return;
    }
    const fetchCart = async () => {
      try {
        const { data } = await axios.get("/orders/cart");
        const products = data?.order?.products || [];
        const count = products.reduce((s: number, p: any) => s + (p.quantity || 0), 0);
        setCartCount(count);
      } catch {
        setCartCount(0);
      }
    };
    fetchCart();
  }, [isLoggedIn]);

  useEffect(() => {
    if (!accountOpen) return;
    const onClick = (e: MouseEvent) => {
      if (accountRef.current && !accountRef.current.contains(e.target as Node)) {
        setAccountOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [accountOpen]);

  const goToShop = () => { window.location.href = "/shop"; };
  const handleLogout = async () => {
    try {
      await axios.post("/auth/logout", {}, { withCredentials: true });
      authContext?.setUser(null);
      window.location.href = "/";
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const logoUrl = storeConfig?.branding?.logo || "";
  const freeShippingMin = storeConfig?.shipping?.minOrderForFreeShipping;
  const announcement = freeShippingMin
    ? `Free shipping on orders over ${storeConfig?.currency?.symbol || "$"}${freeShippingMin}`
    : null;

  return (
    <div className="flex flex-col min-h-screen bg-white">
      {/* Announcement bar */}
      {announcement && (
        <div className="bg-blue-600 text-white text-xs text-center py-2 px-4">
          {announcement}
        </div>
      )}

      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between gap-4">
          <button
            onClick={goToShop}
            className="flex items-center shrink-0"
            aria-label="Home"
          >
            {logoUrl ? (
              <img src={logoUrl} alt="Store Logo" className="h-9 w-auto" />
            ) : (
              <span className="text-xl font-bold text-gray-800 tracking-tight">STORE NAME</span>
            )}
          </button>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-2">
            {isLoggedIn ? (
              <>
                <button
                  onClick={() => (window.location.href = "/shop")}
                  className="px-3 py-1.5 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 transition"
                >
                  Shop
                </button>
                <button
                  onClick={() => (window.location.href = "/shop/cart")}
                  className="relative inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 transition"
                  aria-label="Cart"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  Cart
                  {cartCount > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-blue-600 text-white text-xs font-bold tabular-nums">
                      {cartCount}
                    </span>
                  )}
                </button>
                <div className="relative" ref={accountRef}>
                  <button
                    onClick={() => setAccountOpen((v) => !v)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 transition"
                    aria-haspopup="menu"
                    aria-expanded={accountOpen}
                  >
                    Account
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 011.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                    </svg>
                  </button>
                  {accountOpen && (
                    <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-40">
                      <button
                        onClick={() => (window.location.href = "/shop/profile")}
                        className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        My info
                      </button>
                      <button
                        onClick={() => (window.location.href = "/shop/profile?tab=addresses")}
                        className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Addresses
                      </button>
                      <button
                        onClick={() => (window.location.href = "/shop/profile?tab=orders")}
                        className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Past orders
                      </button>
                      <div className="border-t border-gray-100 my-1" />
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <button
                  onClick={() => (window.location.href = "/shop/register")}
                  className="px-3 py-1.5 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 transition"
                >
                  Register for Wholesale
                </button>
                <button
                  onClick={() => (window.location.href = "/login")}
                  className="px-4 py-1.5 rounded-md text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 transition"
                >
                  Sign in
                </button>
              </>
            )}
          </nav>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden p-2 rounded-md hover:bg-gray-100 text-gray-700"
            aria-label="Open menu"
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
            )}
          </button>
        </div>

        {/* Mobile drawer */}
        {mobileOpen && (
          <div className="md:hidden border-t border-gray-200 bg-white px-4 py-3 space-y-1">
            {isLoggedIn ? (
              <>
                <button onClick={() => (window.location.href = "/shop")} className="block w-full text-left px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100">Shop</button>
                <button onClick={() => (window.location.href = "/shop/cart")} className="flex w-full justify-between items-center px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100">
                  <span>Cart</span>
                  {cartCount > 0 && <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-blue-600 text-white text-xs font-bold tabular-nums">{cartCount}</span>}
                </button>
                <button onClick={() => (window.location.href = "/shop/profile")} className="block w-full text-left px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100">Account</button>
                <button onClick={handleLogout} className="block w-full text-left px-3 py-2 rounded-md text-sm font-medium text-red-600 hover:bg-red-50">Logout</button>
              </>
            ) : (
              <>
                <button onClick={() => (window.location.href = "/shop/register")} className="block w-full text-left px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100">Register for Wholesale</button>
                <button onClick={() => (window.location.href = "/login")} className="block w-full text-left px-3 py-2 rounded-md text-sm font-semibold bg-blue-600 text-white">Sign in</button>
              </>
            )}
          </div>
        )}
      </header>

      <main className="flex-grow">{children}</main>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 mt-auto">
        <div
          className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-1 md:grid-cols-3 gap-8 text-sm [&_h2]:text-base [&_h2]:font-semibold [&_h2]:text-white [&_h2]:mb-3 [&_a]:text-gray-300 [&_a:hover]:text-white [&_a]:underline-offset-2 [&_a:hover]:underline [&_p]:mb-1 [&>p:last-child]:col-span-full [&>p:last-child]:border-t [&>p:last-child]:border-gray-800 [&>p:last-child]:pt-4 [&>p:last-child]:text-center [&>p:last-child]:text-xs [&>p:last-child]:text-gray-500"
          dangerouslySetInnerHTML={{ __html: footerHtml }}
        />
      </footer>
    </div>
  );
}
